<template>
    <div>
        <div class="row form-group">
            <div class="col-12">
                <hr />
            </div>
        </div>

        <div class="row form-group">
            <div class="col-12">
                <label>{{resource('JourneyBuilder_TimerSettings_DailyFrequency')}}</label>
            </div>
        </div>

        <div class="row form-group">
            <div class="col-6">
                <settings-property>
                    <template #label="{id}">
                        <label :for="id">{{resource('JourneyBuilder_TimerSettings_DailyFrequency_Frequency_Label')}}</label>
                    </template>

                    <template #input="{id}">
                        <v-select
                            class="form-control"

                            v-model="frequencyModel"
                            :options="FREQUENCY_OPTIONS"
                            :disabled="!canWrite"
                            :clearable="false"
                            
                            :input-id="id"

                            searchable
                            filterable
                        />
                    </template>
                    
                    <template #errors>
                        <required-error-message v-if="$v.$dirty && $v.frequency.$invalid" />
                    </template>
                </settings-property>
            </div>
        </div>

        <template v-if="frequencyModel === FREQUENCIES.MULTIPLE_TIMES">
            <div class="row form-group">
                <div class="col-2">
                    <label>{{resource('JourneyBuilder_TimerSettings_DailyFrequency_Recurrence_Label')}}</label>
                </div>

                <div class="col-10">
                    <div class="row">
                        <div class="col-5">
                            <v-select
                                class="form-control"

                                v-model="recursInUnitModel"
                                :options="(recurrenceUnitModel && recurrenceUnitModel.recurrenceOptions) || []"
                                :disabled="!canWrite"
                                :clearable="false"

                                searchable
                                filterable
                            />
                        </div>
                        <div class="col-7">
                            <v-select
                                class="form-control"

                                v-model="recurrenceUnitModel"
                                :options="RECURRENCE_UNIT_OPTIONS"
                                :disabled="!canWrite"
                                :clearable="false"

                                searchable
                                filterable
                            />
                        </div>
                    </div>
                </div>
                
                <div class="offset-2 col-10">
                    <required-error-message v-if="$v.$dirty && $v.recursInSeconds.$invalid" />
                </div>
            </div>

            <div class="row form-group">
                <div class="col-6">
                    <settings-property>
                        <template #label>
                            <label>{{resource('JourneyBuilder_TimerSettings_DailyFrequency_StartTime_Label')}}</label>
                        </template>
                        <template #input>
                            <time-picker v-model="startTimeModel" :readonly="!canWrite" />
                        </template>
                        <template #errors>
                            <required-error-message v-if="$v.$dirty && $v.startTime.$invalid" />
                        </template>
                    </settings-property>
                </div>

                <div class="col-6">
                    <settings-property>
                        <template #label>
                            <label>{{resource('JourneyBuilder_TimerSettings_DailyFrequency_EndTime_Label')}}</label>
                        </template>
                        <template #input>
                            <time-picker 
                                v-model="endTimeModel"
                                :disabled-to="startTimeModel"
                                
                                :readonly="!canWrite"
                            />
                        </template>
                        <template #errors>
                            <required-error-message 
                                v-if="$v.$dirty && !$v.endTime.required"
                                class="d-block"
                            />
                            
                            <equal-or-greater-error-message 
                                v-if="$v.$dirty && !$v.endTime.isOnOrAfterStartTime" 
                                
                                class="d-block"
                                
                                :field-name="resource('JourneyBuilder_TimerSettings_DailyFrequency_EndTime_Label')"
                                :other-field-name="resource('JourneyBuilder_TimerSettings_DailyFrequency_StartTime_Label')"
                            />
                        </template>
                    </settings-property>
                </div>
            </div>
        </template>

        <template v-if="frequencyModel === FREQUENCIES.ONCE">
            <div class="row">
                <div class="col-6">
                    <settings-property>
                        <template #label>
                            <label>{{resource('JourneyBuilder_TimerSettings_DailyFrequency_StartTimeOnce_Label')}}</label>
                        </template>
                        <template #input>
                            <time-picker v-model="startTimeModel" />
                        </template>
                        <template #errors>
                            <required-error-message v-if="$v.$dirty && $v.startTime.$invalid" />
                        </template>
                    </settings-property>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { buildTimerDailyFrequencyValidatorSet } from './validations';
import { timerCheckpoint } from "@modules/JourneyBuilder/Bricks/Checkpoints";

import { validationMixin } from 'vuelidate';
import permissionsMixin from "@modules/JourneyBuilder/Mixins/journey-permissions";
import resourceMixin from "@core/Util/Mixins/resources";

import SettingsProperty from "../SettingsProperty";
import TimePicker from "@core/Components/Inputs/TimePicker";
import VSelect from 'vue-select';
import RequiredErrorMessage from "@core/Components/Errors/RequiredErrorMessage";
import EqualOrGreaterErrorMessage from "@core/Components/Errors/EqualOrGreaterErrorMessage";

const FREQUENCIES = timerCheckpoint.DAILY_FREQUENCIES;
const RECURRENCE_UNITS = timerCheckpoint.DAILY_RECURRENCE_UNITS; 

const FREQUENCY_OPTIONS = Object.values(FREQUENCIES);
const RECURRENCE_UNIT_OPTIONS = Object.values(RECURRENCE_UNITS);

export default {
    name: "BrickTimerDailyFrequencySettings",
    
    mixins: [validationMixin, permissionsMixin, resourceMixin],
    
    components: {
        EqualOrGreaterErrorMessage,
        RequiredErrorMessage,
        SettingsProperty,
        TimePicker,
        VSelect
    },
    
    props: {
        frequency: {
            type: String,
            required: false
        },

        recursInSeconds: {
            type: Number,
            required: false
        },

        recurrenceUnit: {
            type: String,
            required: false
        },
        
        startTime: {
            type: Object,
            required: false
        },
        
        endTime: {
            type: Object,
            required: false
        }
    },
    
    data() {
        return {
            FREQUENCIES,
            FREQUENCY_OPTIONS,
            RECURRENCE_UNIT_OPTIONS
        }
    },
    
    computed: {
       frequencyModel: {
            get() {
                return FREQUENCY_OPTIONS.find(option => option.id === this.frequency);
            },
            set(value) {
                this.$emit('update:frequency', value.id);
            }
       },
   
        recurrenceUnitModel: {
            get() {
                return RECURRENCE_UNIT_OPTIONS.find(option => option.id === this.recurrenceUnit);
            },
            set(value) {
                this.$emit('update:recurrenceUnit', value.id);
            }
        },
   
        recursInUnitModel: {
            get() {
               if(!this.recursInSeconds || !this.recurrenceUnitModel) {
                   return undefined;
               }
               
               return this.recurrenceUnitModel.fromSeconds(this.recursInSeconds);
            },
            set(value) {
                if(value && this.recurrenceUnitModel) {
                    value = this.recurrenceUnitModel.toSeconds(value);
                }
                
                this.$emit('update:recursInSeconds', value);
            }
        },
        
        startTimeModel: {
            get() {
                return this.startTime;
            },
            set(value) {
                this.$emit('update:startTime', value);
            }
        },
        
        endTimeModel: {
            get() {
                return this.endTime;
            },
            set(value) {
                this.$emit('update:endTime', value);
            }
        }
    },
    
    validations: buildTimerDailyFrequencyValidatorSet(vm => vm),

    watch: {
        frequencyModel() {
            //Reset all frequency dependent properties
            this.$emit('update:recursInSeconds', undefined);
            this.$emit('update:recurrenceUnit', undefined);
            this.$emit('update:startTime', { hour: undefined, minute: undefined, second: undefined });
            this.$emit('update:endTime', { hour: undefined, minute: undefined, second: undefined });
        },

        recurrenceUnitModel() {
            //Reset our recurs in seconds - prevent accidental funky conversions
            this.$emit('update:recursInSeconds', undefined);
        }
    },

    methods: {
        displayErrors() {
            this.$v.$touch();
        }
    }
}
</script>
